import type { BrowserAgent } from '@newrelic/browser-agent/loaders/browser-agent';

/**
 * Single browser agent instance for the page.
 *
 * This used to be `MicroAgent`, which only loads the jserrors, genericEvents,
 * metrics and logging features — it never loads `ajax`, and `ajax` is the only
 * feature that injects `traceparent` / `tracestate` onto outbound fetch and
 * XHR. That meant `distributed_tracing` below was inert and same-origin calls
 * to `/api/espresso/...` reached Kraken with no trace context, so Kraken
 * started a new trace root instead of continuing the browser's (SR-420).
 *
 * The full `BrowserAgent` loader auto-instruments fetch/XHR, so trace headers
 * are injected and the browser -> Kraken -> backend waterfall stays connected.
 * It must be a singleton: the loader patches page globals, so a second
 * instance would double-report.
 */
const agentConfig = {
  accountID: process.env.NEXT_PUBLIC_NEW_RELIC_ACCOUNT_ID || '',
  agentID: process.env.NEXT_PUBLIC_NEW_RELIC_AGENT_ID || '',
  applicationID: process.env.NEXT_PUBLIC_NEW_RELIC_APP_ID || '',
  licenseKey: process.env.NEXT_PUBLIC_NEW_RELIC_BROWSER_LICENSE_KEY || '',
  trustKey: process.env.NEXT_PUBLIC_NEW_RELIC_TRUST_KEY || '',
};

const isConfigured = Object.values(agentConfig).every(Boolean);

let agent: BrowserAgent | undefined;
let pending: Promise<BrowserAgent | undefined> | undefined;

async function createAgent(): Promise<BrowserAgent | undefined> {
  if (typeof window === 'undefined' || !isConfigured) return undefined;
  if (agent) return agent;

  const { BrowserAgent: Loader } = await import(
    '@newrelic/browser-agent/loaders/browser-agent'
  );

  // Re-check: another caller may have finished while we awaited the import.
  if (agent) return agent;

  agent = new Loader({
    info: {
      applicationID: agentConfig.applicationID,
      beacon: 'bam.nr-data.net',
      errorBeacon: 'bam.nr-data.net',
      licenseKey: agentConfig.licenseKey,
      sa: 1,
    },
    init: {
      // Without this the agent instruments its own beacon requests.
      ajax: { deny_list: ['bam.nr-data.net'] },
      distributed_tracing: { enabled: true },
    },
    loader_config: {
      accountID: agentConfig.accountID,
      agentID: agentConfig.agentID,
      applicationID: agentConfig.applicationID,
      licenseKey: agentConfig.licenseKey,
      trustKey: agentConfig.trustKey,
    },
  });

  return agent;
}

/**
 * Start the agent. Call as early as possible on the client: fetches made
 * before the agent loads are not instrumented and carry no trace headers.
 */
export function initBrowserMonitoring(): Promise<BrowserAgent | undefined> {
  if (!pending) pending = createAgent();
  return pending;
}

export class BrowserMonitoringService {
  public async log(
    level: 'INFO' | 'ERROR' | 'WARN' | 'DEBUG',
    message: string,
  ): Promise<void> {
    try {
      const instance = await initBrowserMonitoring();
      await instance?.log(message, { level: level });
    } catch {}
  }
}
